import React, { createElement } from 'react';
import { OrderLine, Client } from '../types';
import jsPDF from 'jspdf';
const LOGO_URL = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/3ohgcyj2jmqea8d8jx7yts/5c95d907-3611-40dd-8327-34441e9c2b84/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png";
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const w = img.naturalWidth || 100;
    const h = img.naturalHeight || 100;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    // Draw on white background for clean print
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0);
    // Convert to high-contrast black & white for dot matrix clarity
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      const bw = gray > 140 ? 255 : 0;
      data[i] = bw;
      data[i + 1] = bw;
      data[i + 2] = bw;
      data[i + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
const COMPANY = {
  name: 'MILADYS RODRIGUEZ Y ASOCIADOS, SRL',
  address: 'Av. Antonio Guzman No. 102, Edif. Don Paso Torres, Bella Vista, Santiago',
  phone: '809-247-2104',
  almacen: 'ALMACEN PRINCIPAL',
  rnc: '1-30-32929-1'
};
const ITBIS_RATE = 0.18;
function fmt(amount: number): string {
  return amount.toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
function padRight(text: string, len: number): string {
  return text.length >= len ? text.substring(0, len) : text + ' '.repeat(len - text.length);
}
function padLeft(text: string, len: number): string {
  return text.length >= len ? text.substring(0, len) : ' '.repeat(len - text.length) + text;
}
interface CotizacionParams {
  numeroPedido: string;
  farmacia: string;
  vendedor: string;
  fecha: string;
  lineas: OrderLine[];
  clientData?: Partial<Client> | null;
}
export async function generateCotizacionPDF(params: CotizacionParams): Promise<void> {
  const {
    numeroPedido,
    farmacia,
    vendedor,
    fecha,
    lineas,
    clientData
  } = params;
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const ml = 10; // left margin
  const mr = 10; // right margin
  const contentW = pw - ml - mr;
  // ── All black, monospace, no fills ──────────────────────────────────────
  const black = [0, 0, 0] as const;
  doc.setTextColor(...black);
  doc.setDrawColor(...black);
  const font = 'courier';
  const lineH = 5; // line height for body text
  let y = 12;
  // ── Load logo (high-contrast B&W for dot matrix) ────────────────────────
  const logoBase64 = await getLogoBase64();
  // ══════════════════════════════════════════════════════════════════════════
  // HEADER — Company info + Logo
  // ══════════════════════════════════════════════════════════════════════════
  // Top border
  doc.setLineWidth(0.5);
  doc.line(ml, y, pw - mr, y);
  y += 1;
  // Logo on the right side
  const logoSize = 20;
  if (logoBase64) {
    doc.addImage(logoBase64, 'PNG', pw - mr - logoSize - 2, y + 1, logoSize, logoSize);
  }
  // Company name — large and bold (left side, leaving space for logo)
  doc.setFont(font, 'bold');
  doc.setFontSize(13);
  doc.text(COMPANY.name, ml + 2, y + 5);
  y += 7;
  // Address
  doc.setFont(font, 'normal');
  doc.setFontSize(9);
  doc.text(COMPANY.address, ml + 2, y + 4);
  y += 5;
  // Phone + RNC on same line
  doc.text('Tel: ' + COMPANY.phone + '     RNC: ' + COMPANY.rnc, ml + 2, y + 4);
  y += 5;
  // Almacen
  doc.setFont(font, 'bold');
  doc.text(COMPANY.almacen, ml + 2, y + 4);
  y += 6;
  // Bottom border of header
  doc.setLineWidth(0.5);
  doc.line(ml, y, pw - mr, y);
  y += 2;
  // ══════════════════════════════════════════════════════════════════════════
  // COTIZACION NUMBER + DATE
  // ══════════════════════════════════════════════════════════════════════════
  doc.setFont(font, 'bold');
  doc.setFontSize(12);
  doc.text('COTIZACION: ' + (numeroPedido || 'S/N'), ml + 2, y + 5);
  const fechaFormatted = new Date(fecha + 'T00:00:00').toLocaleDateString('es-DO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  doc.text('FECHA: ' + fechaFormatted, pw - mr - 2, y + 5, {
    align: 'right'
  });
  y += 8;
  // Separator
  doc.setLineWidth(0.3);
  doc.line(ml, y, pw - mr, y);
  y += 2;
  // ══════════════════════════════════════════════════════════════════════════
  // CLIENT INFO
  // ══════════════════════════════════════════════════════════════════════════
  const clientCode = clientData?.codigo || '';
  const clientRnc = clientData?.rnc || clientData?.cedula || '';
  const clientPhone = clientData?.telefono || '';
  const clientAddress = clientData?.direccion || '';
  const clientCity = clientData?.ciudad || '';
  doc.setFont(font, 'bold');
  doc.setFontSize(10);
  // Cliente
  const clientLabel = clientCode ? '(' + clientCode + ') ' + farmacia : farmacia;
  doc.text('CLIENTE : ' + clientLabel, ml + 2, y + 4);
  y += lineH;
  // RNC Cliente
  doc.text('RNC     : ' + (clientRnc || '---'), ml + 2, y + 4);
  // Phone on right
  doc.text('TEL: ' + (clientPhone || '---'), pw - mr - 2, y + 4, {
    align: 'right'
  });
  y += lineH;
  // Address
  doc.setFont(font, 'normal');
  const fullAddr = clientAddress + (clientCity ? ', ' + clientCity : '');
  if (fullAddr) {
    const addrLines = doc.splitTextToSize('DIR     : ' + fullAddr, contentW - 4);
    doc.text(addrLines, ml + 2, y + 4);
    y += lineH * addrLines.length;
  } else {
    doc.text('DIR     : ---', ml + 2, y + 4);
    y += lineH;
  }
  // Vendedor
  doc.setFont(font, 'bold');
  doc.text('VENDEDOR: ' + vendedor, ml + 2, y + 4);
  y += lineH + 1;
  // Separator
  doc.setLineWidth(0.5);
  doc.line(ml, y, pw - mr, y);
  y += 1;
  // Nota line
  doc.setFont(font, 'normal');
  doc.setFontSize(9);
  doc.text('NOTA: _______________________________________________', ml + 2, y + 4);
  y += 6;
  // Separator
  doc.setLineWidth(0.3);
  doc.line(ml, y, pw - mr, y);
  y += 2;
  // ══════════════════════════════════════════════════════════════════════════
  // TABLE — Simple text-based columns for dot matrix clarity
  // ══════════════════════════════════════════════════════════════════════════
  // Column widths (mm)
  const colCant = 14;
  const colDesc = 68;
  const colRef = 28;
  const colPrecio = 22;
  const colItbis = 22;
  const colMonto = contentW - colCant - colDesc - colRef - colPrecio - colItbis;
  // Table header
  doc.setFont(font, 'bold');
  doc.setFontSize(9);
  let cx = ml;
  doc.text('CANT', cx + 2, y + 4);
  cx += colCant;
  doc.text('DESCRIPCION', cx + 2, y + 4);
  cx += colDesc;
  doc.text('REF', cx + 2, y + 4);
  cx += colRef;
  doc.text('PRECIO', cx + colPrecio - 2, y + 4, {
    align: 'right'
  });
  cx += colPrecio;
  doc.text('ITBIS', cx + colItbis - 2, y + 4, {
    align: 'right'
  });
  cx += colItbis;
  doc.text('MONTO', cx + colMonto - 2, y + 4, {
    align: 'right'
  });
  y += 6;
  // Header underline — double line for emphasis
  doc.setLineWidth(0.5);
  doc.line(ml, y, pw - mr, y);
  y += 0.8;
  doc.setLineWidth(0.3);
  doc.line(ml, y, pw - mr, y);
  y += 2;
  // ── Table rows ──────────────────────────────────────────────────────────
  let grandSubtotal = 0;
  let grandItbis = 0;
  doc.setFont(font, 'normal');
  doc.setFontSize(9);
  lineas.forEach(linea => {
    // Page break check — leave space for totals and signatures
    if (y + lineH + 2 > ph - 55) {
      doc.addPage();
      y = 12;
      // Reprint header on new page
      doc.setFont(font, 'bold');
      doc.setFontSize(9);
      cx = ml;
      doc.text('CANT', cx + 2, y + 4);
      cx += colCant;
      doc.text('DESCRIPCION', cx + 2, y + 4);
      cx += colDesc;
      doc.text('REF', cx + 2, y + 4);
      cx += colRef;
      doc.text('PRECIO', cx + colPrecio - 2, y + 4, {
        align: 'right'
      });
      cx += colPrecio;
      doc.text('ITBIS', cx + colItbis - 2, y + 4, {
        align: 'right'
      });
      cx += colItbis;
      doc.text('MONTO', cx + colMonto - 2, y + 4, {
        align: 'right'
      });
      y += 6;
      doc.setLineWidth(0.5);
      doc.line(ml, y, pw - mr, y);
      y += 2;
      doc.setFont(font, 'normal');
    }
    const lineSubtotal = linea.cantidad * linea.precio;
    const lineItbis = Math.round(lineSubtotal * ITBIS_RATE * 100) / 100;
    const lineMonto = Math.round((lineSubtotal + lineItbis) * 100) / 100;
    grandSubtotal += lineSubtotal;
    grandItbis += lineItbis;
    cx = ml;
    // Cantidad
    doc.text(String(linea.cantidad), cx + colCant - 3, y + 4, {
      align: 'right'
    });
    cx += colCant;
    // Descripcion — truncate to fit
    let descText = linea.nombre;
    const maxDescMm = colDesc - 4;
    while (doc.getTextWidth(descText) > maxDescMm && descText.length > 3) {
      descText = descText.slice(0, -1);
    }
    if (descText !== linea.nombre) descText = descText.trim() + '..';
    doc.text(descText, cx + 2, y + 4);
    cx += colDesc;
    // Referencia (SKU)
    let skuText = linea.sku || '';
    const maxSkuMm = colRef - 4;
    while (doc.getTextWidth(skuText) > maxSkuMm && skuText.length > 3) {
      skuText = skuText.slice(0, -1);
    }
    if (skuText !== (linea.sku || '')) skuText = skuText.trim() + '..';
    doc.text(skuText, cx + 2, y + 4);
    cx += colRef;
    // Precio
    doc.text(fmt(linea.precio), cx + colPrecio - 2, y + 4, {
      align: 'right'
    });
    cx += colPrecio;
    // ITBIS
    doc.text(fmt(lineItbis), cx + colItbis - 2, y + 4, {
      align: 'right'
    });
    cx += colItbis;
    // Monto
    doc.setFont(font, 'bold');
    doc.text(fmt(lineMonto), cx + colMonto - 2, y + 4, {
      align: 'right'
    });
    doc.setFont(font, 'normal');
    y += lineH;
    // Row separator — simple dotted line
    doc.setLineWidth(0.15);
    doc.setLineDashPattern([0.5, 1], 0);
    doc.line(ml, y, pw - mr, y);
    doc.setLineDashPattern([], 0);
    y += 1;
  });
  // ── Bottom border of table ──────────────────────────────────────────────
  y += 1;
  doc.setLineWidth(0.5);
  doc.line(ml, y, pw - mr, y);
  y += 0.8;
  doc.setLineWidth(0.3);
  doc.line(ml, y, pw - mr, y);
  // ══════════════════════════════════════════════════════════════════════════
  // TOTALS — right-aligned, clear text
  // ══════════════════════════════════════════════════════════════════════════
  const grandTotal = Math.round((grandSubtotal + grandItbis) * 100) / 100;
  const totalItems = lineas.reduce((s, l) => s + l.cantidad, 0);
  // Ensure space for totals + signatures
  if (y + 45 > ph - 35) {
    doc.addPage();
    y = 12;
  }
  y += 6;
  const labelX = pw - mr - 65;
  const valX = pw - mr - 2;
  // Items count on left
  doc.setFont(font, 'normal');
  doc.setFontSize(9);
  doc.text('TOTAL ITEMS: ' + totalItems + ' unidades', ml + 2, y + 4);
  // Subtotal
  doc.setFont(font, 'normal');
  doc.setFontSize(10);
  doc.text('SUBTOTAL:', labelX, y + 4, {
    align: 'right'
  });
  doc.setFont(font, 'bold');
  doc.text(fmt(grandSubtotal), valX, y + 4, {
    align: 'right'
  });
  y += 6;
  // ITBIS
  doc.setFont(font, 'normal');
  doc.text('+ ITBIS:', labelX, y + 4, {
    align: 'right'
  });
  doc.setFont(font, 'bold');
  doc.text(fmt(grandItbis), valX, y + 4, {
    align: 'right'
  });
  y += 6;
  // Separator before total
  doc.setLineWidth(0.3);
  doc.line(labelX - 10, y, pw - mr, y);
  y += 2;
  // TOTAL — large and bold
  doc.setFont(font, 'bold');
  doc.setFontSize(12);
  doc.text('TOTAL DOP:', labelX, y + 5, {
    align: 'right'
  });
  doc.setFontSize(13);
  doc.text(fmt(grandTotal), valX, y + 5, {
    align: 'right'
  });
  y += 8;
  // Underline total
  doc.setLineWidth(0.5);
  doc.line(labelX - 10, y, pw - mr, y);
  // ══════════════════════════════════════════════════════════════════════════
  // SIGNATURE LINES
  // ══════════════════════════════════════════════════════════════════════════
  const sigY = Math.max(y + 22, ph - 38);
  const sigW = (contentW - 16) / 3;
  doc.setLineWidth(0.4);
  doc.setFont(font, 'bold');
  doc.setFontSize(9);
  // Line 1 — Despachado por
  doc.line(ml, sigY, ml + sigW, sigY);
  doc.text('Despachado por', ml + sigW / 2, sigY + 5, {
    align: 'center'
  });
  // Line 2 — Realizado por
  const sig2X = ml + sigW + 8;
  doc.line(sig2X, sigY, sig2X + sigW, sigY);
  doc.text('Realizado por', sig2X + sigW / 2, sigY + 5, {
    align: 'center'
  });
  // Line 3 — Recibido por
  const sig3X = sig2X + sigW + 8;
  doc.line(sig3X, sigY, sig3X + sigW, sigY);
  doc.text('Recibido por', sig3X + sigW / 2, sigY + 5, {
    align: 'center'
  });
  // ══════════════════════════════════════════════════════════════════════════
  // DISCLAIMER
  // ══════════════════════════════════════════════════════════════════════════
  const disclaimerY = sigY + 10;
  doc.setFont(font, 'normal');
  doc.setFontSize(7);
  const disclaimer = 'No se aceptan reclamos en factura luego de 7 dias de recibida. Es su responsabilidad devolver los productos vencidos dentro de los tiempos establecidos.';
  const disclaimerLines = doc.splitTextToSize(disclaimer, contentW);
  doc.text(disclaimerLines, ml + 2, disclaimerY);
  // ── Open PDF ────────────────────────────────────────────────────────────
  const blobUrl = doc.output('bloburl');
  window.open(String(blobUrl), '_blank');
}